import React from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { createSelector } from "reselect";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

//import Scss
import "./assets/scss/themes.scss";

//imoprt Route
import Route from "./Routes";

// Flash Notification System
import FlashContainer from "./Components/Common/FlashNotification/FlashContainer";

// Toast Notification System (legacy — used by Bootstrap-era pages)
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Sonner Toaster (modern stack — used by features/* migrated to shadcn)
import { Toaster as SonnerToaster } from "@/shared/ui/sonner";
// Field-app theme (dark/light) — used to theme toasts on /field routes.
import { useTheme as useFieldTheme } from "@/features/field-app/theme";

// Internal-only environment indicator (cryptic corner dot: amber=DEV, green=PROD)
import EnvIndicator from "@/shared/ui/env-indicator";

// Error Boundary
import ErrorBoundary from "./Components/Common/ErrorBoundary";

// Hide the internal env indicator on the field-app routes so the mobile shell
// stays clean (its corner dots would otherwise overlap the bottom nav).
function EnvIndicatorGate() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/field")) return null;
  return <EnvIndicator />;
}

// Sonner toasts mount at the app root (outside the field shell), so they don't
// inherit the field-app dark theme. Carry `dark tw-dark` onto the toaster
// container — and set sonner's own theme — whenever the field app is in dark
// mode, so toast alerts match. Desktop routes stay light regardless.
function FieldAwareToaster() {
  const { pathname } = useLocation();
  const { theme } = useFieldTheme();
  const fieldDark = pathname.startsWith("/field") && theme === "dark";
  return (
    <SonnerToaster
      theme={fieldDark ? "dark" : "light"}
      className={fieldDark ? "dark tw-dark field-surface" : undefined}
    />
  );
}

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh
      gcTime: 10 * 60 * 1000, // 10 minutes - cache time (formerly cacheTime)
      refetchOnWindowFocus: false, // Don't refetch on window focus
      retry: 1, // Retry failed requests once
    },
  },
});

function App() {
  // Select layout mode from Redux store
  const selectLayoutState = (state: any) => state.Layout;
  const selectLayoutMode = createSelector(
    selectLayoutState,
    (layout) => layout.layoutModeType || "light"
  );
  const layoutModeType = useSelector(selectLayoutMode);

  // Determine toast theme based on layout mode
  const toastTheme = layoutModeType === "dark" ? "dark" : "light";

  return (
    <ErrorBoundary
      level="global"
      onError={(error, errorInfo) => {
        // Log global errors
        console.error("Global Error Boundary caught an error:", error, errorInfo);
        
        // In production, you might want to send to error tracking service
        // Example: trackGlobalError(error, errorInfo);
      }}
    >
      <QueryClientProvider client={queryClient}>
        <React.Fragment>
          <Route />
          <FlashContainer />
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme={toastTheme}
          />
          <FieldAwareToaster />
          <EnvIndicatorGate />
        </React.Fragment>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;